import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { finalize, switchMap } from 'rxjs/operators';
import { LabelsService } from '../../../core/services/LabelsService';
import { NotificationService } from '../../../core/services';
import { ArticlesVariantsService } from '../../labels-printer/core/services';
import { ArticleLabelsService } from '../../article-labels/core/services/ArticleLabelsService';
import { IEtikettOverview } from '../core/interfaces/IEtikettOverview';
import { canPreviewEtikett, isPersistedArticleLabel } from '../core/etikett-row';
import { ILabelPreviewHtml } from '../../../core/interfaces';
import { IArticleVariant } from '../../labels-printer/core/interfaces';

@Component({
  selector: 'app-etikett-preview-modal',
  templateUrl: './etikett-preview-modal.component.html',
  styleUrl: './etikett-preview-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EtikettPreviewModalComponent {
  public isVisible = false;
  public loading = false;
  public title = 'Vorschau';
  public preview: ILabelPreviewHtml = null;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly labelsService: LabelsService,
    private readonly notify: NotificationService,
    private readonly variantsService: ArticlesVariantsService,
    private readonly articleLabelsService: ArticleLabelsService,
  ) {}

  public showModal(row: IEtikettOverview): void {
    if (!canPreviewEtikett(row)) {
      return;
    }

    this.title = row.etikettenartName
      ? `Vorschau · ${row.etikettenartName}`
      : 'Vorschau';
    this.preview = null;
    this.isVisible = true;
    this.loading = true;
    this.cdr.markForCheck();

    this.loadPreview(row).pipe(
      finalize(() => {
        this.loading = false;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: preview => {
        this.preview = preview;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notify.pushError('Vorschau', 'Vorschau konnte nicht geladen werden.');
        this.cdr.markForCheck();
      },
    });
  }

  public handleCancel(): void {
    this.isVisible = false;
    this.preview = null;
    this.cdr.markForCheck();
  }

  private loadPreview(row: IEtikettOverview) {
    if (row.source === 'OwnBrand') {
      return this.variantsService.getById(String(row.articleVariantId)).pipe(
        switchMap(variant => this.labelsService.previewHtml(this.ownBrandBody(variant))),
      );
    }

    if (isPersistedArticleLabel(row)) {
      this.articleLabelsService.setBaseUrl(row.articleId);
      return this.articleLabelsService.getById(String(row.id)).pipe(
        switchMap(label => this.labelsService.previewHtml({
          articleLabelType: label.type ?? row.etikettenart,
          articleVariantId: label.articleVariantId ?? row.articleVariantId,
          context: label.context || '{}',
        })),
      );
    }

    return this.labelsService.previewHtml({
      articleLabelType: row.etikettenart,
      articleVariantId: row.articleVariantId,
      context: '{}',
    });
  }

  private ownBrandBody(variant: IArticleVariant): Record<string, unknown> {
    return {
      ...(variant.labelInfo as object || {}),
      ean: variant.ean || '',
      storageLocation: variant.storageLocation || '',
      labelType: variant.labelInfo?.labelType ?? variant.labelType,
    };
  }
}
